import type { SetupSection } from './fieldTypes';

const MONEY = { regex: /^\d+(\.\d{1,2})?$/, message: 'Enter an amount, e.g. 1.00' };
const WHOLE = { regex: /^\d+$/, message: 'Whole number' };
const ROUNDING = [
  { value: 'DOWN', label: 'Round down to whole units' },
  { value: 'NEAREST', label: 'Round to nearest' },
  { value: 'UP', label: 'Round up' },
];

export const CLASS_SECTION: SetupSection = {
  id: 'class',
  group: 'CLASS',
  title: 'Unit class',
  summary: 'The security investors hold: price, precision, partly paid terms and certificates.',
  blocks: [
    {
      title: 'Class',
      fields: [
        { key: 'class.name', label: 'Class name', kind: 'text', required: true },
        {
          key: 'class.code',
          label: 'Class code',
          kind: 'text',
          required: true,
          pattern: { regex: /^[A-Z0-9]{1,4}$/, message: '1–4 capital letters or digits' },
        },
        {
          key: 'class.category',
          label: 'Category',
          kind: 'select',
          options: [
            { value: 'ORDINARY', label: 'Ordinary units' },
            { value: 'INCOME', label: 'Income units' },
            { value: 'PREFERENCE', label: 'Preference units' },
          ],
        },
        { key: 'class.capTableOrder', label: 'Cap table order', kind: 'number', pattern: WHOLE },
        {
          key: 'class.issuePrice',
          label: 'Issue price ($)',
          kind: 'text',
          required: true,
          pattern: MONEY,
        },
        { key: 'class.targetReturn', label: 'Target return (% p.a.)', kind: 'number' },
        { key: 'class.fee', label: 'Management fee (% p.a.)', kind: 'number' },
      ],
    },
    {
      title: 'Rounding and precision',
      fields: [
        {
          key: 'class.appRounding',
          label: 'Application rounding',
          kind: 'select',
          options: ROUNDING,
        },
        {
          key: 'class.valueRounding',
          label: 'Application value rounding',
          kind: 'select',
          options: ROUNDING,
        },
        {
          key: 'class.unitRounding',
          label: 'Application unit rounding',
          kind: 'select',
          options: ROUNDING,
        },
        {
          key: 'class.priceRounding',
          label: 'Application price rounding',
          kind: 'select',
          options: ROUNDING,
        },
        {
          key: 'class.redRounding',
          label: 'Redemption rounding',
          kind: 'select',
          options: ROUNDING,
        },
        {
          key: 'class.unitPrecision',
          label: 'Unit decimals',
          kind: 'select',
          options: ['0', '2', '4'].map((value) => ({ value, label: value })),
        },
        {
          key: 'class.pricePrecision',
          label: 'Price decimals',
          kind: 'select',
          options: ['2', '4', '6'].map((value) => ({ value, label: value })),
        },
      ],
    },
    {
      title: 'Partly paid units',
      description: 'Units issued with part of the price paid; the balance is called later.',
      fields: [
        { key: 'class.partlyPaid', label: 'Issue units partly paid', kind: 'switch' },
        {
          key: 'class.paidOnApplication',
          label: 'Paid on application (%)',
          kind: 'number',
          required: true,
          toConfirm: true,
          pattern: { regex: /^([1-9]\d?|100)$/, message: '1–100' },
          showIf: (values) => values['class.partlyPaid'] === true,
        },
      ],
    },
    {
      title: 'Holding statements',
      fields: [
        { key: 'class.asxCode', label: 'ASX code (if listed)', kind: 'text' },
        {
          key: 'class.excludeStatements',
          label: 'Exclude this class from holding statements',
          kind: 'switch',
        },
      ],
    },
    {
      title: 'Unit certificates',
      fields: [
        { key: 'cert.enabled', label: 'Issue unit certificates', kind: 'switch', wide: true },
        {
          key: 'cert.companyCode',
          label: 'Company code',
          kind: 'text',
          required: true,
          showIf: (values) => values['cert.enabled'] === true,
        },
        {
          key: 'cert.securityCode',
          label: 'Security code',
          kind: 'text',
          required: true,
          showIf: (values) => values['cert.enabled'] === true,
        },
        {
          key: 'cert.startSeq',
          label: 'First certificate number',
          kind: 'number',
          pattern: WHOLE,
          showIf: (values) => values['cert.enabled'] === true,
        },
        {
          key: 'cert.title',
          label: 'Certificate title',
          kind: 'text',
          showIf: (values) => values['cert.enabled'] === true,
        },
        {
          key: 'cert.terms',
          label: 'Certificate terms',
          kind: 'textarea',
          wide: true,
          toConfirm: true,
          showIf: (values) => values['cert.enabled'] === true,
        },
      ],
    },
    {
      title: 'Opening prices',
      description: 'Bid (exit), mid (NTA) and offer (entry) prices from the first close.',
      fields: [
        { key: 'price.entry', label: 'Entry price', kind: 'text', required: true, pattern: MONEY },
        { key: 'price.mid', label: 'Mid / NTA', kind: 'text', required: true, pattern: MONEY },
        { key: 'price.exit', label: 'Exit price', kind: 'text', required: true, pattern: MONEY },
        { key: 'price.date', label: 'Effective date', kind: 'date', required: true },
      ],
    },
  ],
};
